import React, { useState, useEffect } from 'react';
import { Search, Filter, Calendar, IndianRupee, ChevronUp, ChevronDown, Plus, Edit2, Trash2, Upload } from 'lucide-react';
import { Expense, ExpenseFilters } from '../types';
import { apiClient } from '../api/client';
import { formatINR } from '../utils/currency';
import CategorySelect from './CategorySelect';
import ExpenseForm from './ExpenseForm';
import BulkUpload from './BulkUpload';

interface ExpenseListProps {
  refreshTrigger?: number; // For triggering refresh from parent
  className?: string;
  onExpenseAdded?: () => void;
}

const ExpenseList: React.FC<ExpenseListProps> = ({ refreshTrigger, className = "", onExpenseAdded }) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState<string>('0');
  const [filters, setFilters] = useState<ExpenseFilters>({
    start_date: '',
    end_date: '',
    category_id: undefined,
    include_descendants: true,
    sort_by: 'date',
    sort_order: 'desc',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; expenseId: number | null; expenseDescription: string }>({
    show: false,
    expenseId: null,
    expenseDescription: ''
  });

  useEffect(() => {
    loadExpenses();
  }, [refreshTrigger, filters]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const [expensesData, totalData] = await Promise.all([
        apiClient.getExpenses(filters),
        apiClient.getExpenseSummary(filters),
      ]);

      setExpenses(expensesData);
      setTotal(totalData.total);
    } catch (error) {
      console.error('Failed to load expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof ExpenseFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSort = (column: string) => {
    setFilters(prev => ({
      ...prev,
      sort_by: column,
      sort_order: prev.sort_by === column && prev.sort_order === 'desc' ? 'asc' : 'desc',
    }));
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      category_id: undefined,
      include_descendants: true,
      sort_by: 'date',
      sort_order: 'desc',
    });
  };

  const getCategoryPath = (expense: Expense): string => {
    // For now, just return the category name
    // In a full implementation, you'd build the full path from the category tree
    return expense.category.name;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getSortIcon = (column: string) => {
    if (filters.sort_by !== column) return null;
    return filters.sort_order === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
  };

  const handleDeleteExpense = (expenseId: number, expenseDescription: string) => {
    setDeleteConfirm({
      show: true,
      expenseId,
      expenseDescription
    });
  };

  const confirmDeleteExpense = async () => {
    if (!deleteConfirm.expenseId) return;

    try {
      await apiClient.deleteExpense(deleteConfirm.expenseId);
      loadExpenses(); // Refresh the list
      setDeleteConfirm({ show: false, expenseId: null, expenseDescription: '' });
    } catch (error) {
      console.error('Failed to delete expense:', error);
      alert('Failed to delete expense. Please try again.');
      setDeleteConfirm({ show: false, expenseId: null, expenseDescription: '' });
    }
  };

  const cancelDeleteExpense = () => {
    setDeleteConfirm({ show: false, expenseId: null, expenseDescription: '' });
  };

  const handleExpenseSaved = () => {
    setEditingExpense(null);
    setShowAddModal(false);
    loadExpenses(); // Refresh the list
  };

  const handleBulkUploadComplete = (result: any) => {
    setShowBulkUpload(false);
    loadExpenses(); // Refresh the list

    // Show success message
    if (result.created > 0) {
      alert(`Successfully uploaded ${result.created} expenses!`);
    }
  };

  return (
    <div className={className}>
      {/* Header and Summary */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">Expenses</h3>
          <p className="text-slate-400 text-sm">
            Total: <span className="text-green-400 font-medium">{formatINR(total)}</span>
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setShowBulkUpload(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            <Upload className="h-4 w-4" />
            Bulk Upload
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-[var(--accent-gold)] hover:bg-[#d4b896] text-white rounded-md transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Expense
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 bg-[var(--accent-gold)]/20 hover:bg-[var(--accent-gold)]/30 border border-[var(--accent-gold)]/30 rounded-md text-[var(--accent-gold)] hover:text-[#d4b896] transition-colors"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={filters.start_date || ''}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={filters.end_date || ''}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Category
              </label>
              <CategorySelect
                value={filters.category_id}
                onChange={(categoryId) => handleFilterChange('category_id', categoryId || undefined)}
                placeholder="All categories"
              />
            </div>

            {/* Include Descendants */}
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={filters.include_descendants}
                  onChange={(e) => handleFilterChange('include_descendants', e.target.checked)}
                  className="rounded border-white/10 bg-white/5 text-blue-500 focus:ring-blue-500"
                />
                Include subcategories
              </label>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-[var(--accent-gold)] hover:bg-[#d4b896] text-white rounded-md transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="text-slate-400">Loading expenses...</div>
        </div>
      )}

      {/* Empty State */}
      {!loading && expenses.length === 0 && (
        <div className="text-center py-8">
          <div className="text-slate-400 mb-4">No expenses found</div>
          {(filters.start_date || filters.end_date || filters.category_id !== undefined) && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-[var(--accent-gold)] hover:bg-[#d4b896] text-white rounded-md transition-colors"
            >
              Clear filters to see all expenses
            </button>
          )}
        </div>
      )}

      {/* Expenses Table */}
      {!loading && expenses.length > 0 && (
        <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
          <div className="overflow-x-auto h-[calc(100vh-400px)] min-h-[500px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer hover:text-white"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center gap-1">
                      Date {getSortIcon('date')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer hover:text-white"
                    onClick={() => handleSort('description')}
                  >
                    <div className="flex items-center gap-1">
                      Description {getSortIcon('description')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th
                    className="px-4 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer hover:text-white"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Amount {getSortIcon('amount')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {expenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-sm text-white">
                      {formatDate(expense.date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">
                      {expense.description || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">
                      {getCategoryPath(expense)}
                    </td>
                    <td className="px-4 py-3 text-sm text-white text-right font-medium">
                      {formatINR(expense.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="p-1 text-slate-400 hover:text-blue-400 transition-colors"
                          title="Edit expense"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.id, expense.description || 'this expense')}
                          className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                          title="Delete expense"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Expense Modal */}
       {(showAddModal || editingExpense) && (
         <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
           <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md mx-4">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-semibold text-white">
                 {editingExpense ? 'Edit Expense' : 'Add Expense'}
               </h3>
               <button
                 onClick={() => {
                   setShowAddModal(false);
                   setEditingExpense(null);
                 }}
                 className="text-slate-400 hover:text-white"
               >
                 ✕
               </button>
             </div>
             <ExpenseForm
               expense={editingExpense}
               onExpenseAdded={handleExpenseSaved}
             />
           </div>
         </div>
       )}

       {/* Delete Confirmation Modal */}
        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-lg p-6 w-full max-w-sm mx-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Delete Expense</h3>
                <p className="text-slate-300 text-sm">
                  Are you sure you want to delete{' '}
                  <span className="text-white font-medium">
                    "{deleteConfirm.expenseDescription}"
                  </span>
                  ? This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={cancelDeleteExpense}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteExpense}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
 
        {/* Bulk Upload Modal */}
        {showBulkUpload && (
          <BulkUpload
            onUploadComplete={handleBulkUploadComplete}
            onClose={() => setShowBulkUpload(false)}
          />
        )}
    </div>
  );
};

export default ExpenseList;